import { useState } from 'react';

import { Typography } from '@mui/material';

import { say } from '@/james-playground/aa-family-claim/claim/asks';
import { QUIET } from '@/james-playground/aa-family-claim/claim/cards/cards.content';
import {
  CardShell,
  ChoiceCards,
} from '@/james-playground/aa-family-claim/claim/cards/parts';
import { type CardProps } from '@/james-playground/aa-family-claim/claim/cards/types';
import {
  known,
  str,
} from '@/james-playground/aa-family-claim/claim/cards/values';

type Pick = (typeof QUIET.options)[number]['value'];

/**
 * The quiet line (spec 4), asked of everyone the same way. Yes puts the claim
 * on the quicker route. "I'd rather talk to an adviser first" carries on with
 * the usual claim and adds the conversation to the adviser's list. The words
 * for what this is about are said once, in the question, and never here.
 */
const QuietCard = ({ ask, facts, voice: v, onSubmit, onSkip }: CardProps) => {
  const [pick, setPick] = useState<Pick | ''>(() => {
    if (str(known(facts, 'talkAboutQuickerRoute')) === 'Yes') return 'talk';
    const value = str(known(facts, 'specialRules'));
    return value === 'Yes' || value === 'No' ? value : '';
  });

  const submit = () => {
    if (!pick) return;
    const label = QUIET.options.find((o) => o.value === pick)?.label ?? '';
    if (pick === 'talk')
      return onSubmit(
        { specialRules: 'No', talkAboutQuickerRoute: 'Yes' },
        label,
      );
    onSubmit({ specialRules: pick, talkAboutQuickerRoute: 'No' }, label);
  };

  return (
    <CardShell
      label={say(ask.title, v)}
      ready={Boolean(pick)}
      onContinue={submit}
      onSkip={onSkip}
    >
      <ChoiceCards
        label={say(ask.prompt, v)}
        options={QUIET.options.map((o) => ({
          value: o.value,
          label: o.label,
          detail:
            o.value === 'Yes' ? (
              <Typography variant='body2'>{QUIET.yesNote}</Typography>
            ) : o.value === 'talk' ? (
              <Typography variant='body2'>{QUIET.talkNote}</Typography>
            ) : undefined,
        }))}
        selected={pick ? [pick] : []}
        onPick={(value) => setPick(value as Pick)}
      />
    </CardShell>
  );
};

QuietCard.displayName = 'QuietCard';
export default QuietCard;
